/**
 * How a date-range selection READS.
 *
 * The vocabulary itself — the preset names, and the arithmetic that turns one
 * into a window — lives in `@clockwise/shared`, because a report spec stores a
 * preset and the API parses it back, and neither of those can import a package
 * that depends on React. What stays here is presentation: the words on the
 * buttons, and the one-line summary a trigger or a chip prints.
 *
 * Re-exported below so the widgets in this folder (and `@clockwise/ui`'s own
 * index) keep a single import for both halves.
 */
import {
  type DateRangePreset,
  type DateRangeValue,
  type ResolvedDateRange,
  resolveDateRange,
} from '@clockwise/shared';
import { format } from 'date-fns';

export {
  ALL_DATE_RANGE_PRESETS,
  DATE_RANGE_PRESETS,
  type DateRangePreset,
  type DateRangeValue,
  type ResolvedDateRange,
  resolveDateRange,
} from '@clockwise/shared';

/**
 * THIS AND LAST, not CURRENT and PREVIOUS.
 *
 * Same six macros, ~20% less width apiece, and the shorter pair is the one
 * people say out loud. The width matters because these are read on a filter
 * bar beside the window they resolve to, where the period chip was the widest
 * thing on a row of chips; it stopped being that here rather than by
 * abbreviating anything into dots.
 *
 * ONE VOCABULARY, so the panel and the chip and the list-page filter agree —
 * a macro picked as "This month" that read back as "Current month" would be
 * two names for a thing somebody just chose by name.
 */
export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  current_month: 'This month',
  previous_month: 'Last month',
  current_quarter: 'This quarter',
  previous_quarter: 'Last quarter',
  current_year: 'This year',
  previous_year: 'Last year',
  last_12_months: 'Last 12 months',
  custom: 'Custom range',
  all_time: 'All time',
  current_week: 'This week',
};

/** "Jun 1 – Jun 3, 2026" label for a resolved range (or `''` if absent). */
export function prettyDateRange(range: ResolvedDateRange | null): string {
  if (!range) return '';
  const f = new Date(`${range.from}T00:00:00`);
  const t = new Date(`${range.to}T00:00:00`);
  const sameYear = f.getFullYear() === t.getFullYear();
  return `${format(f, sameYear ? 'MMM d' : 'MMM d, yyyy')} – ${format(t, 'MMM d, yyyy')}`;
}

/**
 * A window that is exactly one calendar period, named once: "Sep 2026",
 * "Apr–Jun 2026", "2026". `null` for anything else.
 *
 * THE POINT IS THAT IT LOSES NOTHING. "Sep 1 – Sep 30, 2026" and "Sep 2026"
 * are the same fact, one of them said twice — the boundary dates of a whole
 * month are derivable from the month. So this is a shorter rendering rather
 * than an abbreviation, and the `null` is what keeps it that way: a window
 * that is NOT a whole period ("Sep 4 – Sep 22") has no short name, and the
 * caller falls back to printing both dates rather than rounding to one that
 * would be a lie.
 *
 * Every macro resolves to a whole month, quarter or year (`resolveDateRange`
 * builds each from `monthSpan`), so every macro has one — except
 * `current_week`, a Monday-to-Sunday window that is deliberately not a
 * calendar period and correctly gets `null` here.
 */
export function wholePeriodLabel(range: ResolvedDateRange): string | null {
  // Sliced rather than split, so each field is a number and not a
  // `number | undefined` the compiler then has to be argued out of.
  const fy = Number(range.from.slice(0, 4));
  const fm = Number(range.from.slice(5, 7));
  const fd = Number(range.from.slice(8, 10));
  const ty = Number(range.to.slice(0, 4));
  const tm = Number(range.to.slice(5, 7));
  const td = Number(range.to.slice(8, 10));
  if (fd !== 1 || fy !== ty) return null;
  // Day 0 of the NEXT month is the last day of this one — the same trick
  // `@clockwise/shared` uses, and how February takes care of itself.
  if (td !== new Date(Date.UTC(ty, tm, 0)).getUTCDate()) return null;
  const month = (m: number) => format(new Date(fy, m - 1, 1), 'MMM');
  if (fm === 1 && tm === 12) return String(fy);
  if (fm === tm) return `${month(fm)} ${fy}`;
  // A quarter, and only on its own boundaries: Apr–Jun is one, May–Jul is
  // three months that no macro produces and that nothing should name.
  if (tm - fm === 2 && (fm - 1) % 3 === 0) return `${month(fm)}–${month(tm)} ${fy}`;
  return null;
}

/**
 * The two halves of a selection's summary, apart: the macro's NAME, and the
 * WINDOW it resolves to today.
 *
 * Either can be empty, and which one is says what kind of selection this is. A
 * Custom pick has no name — two dates somebody typed are not called anything —
 * so it is all window. "All time" and a half-picked Custom range have no
 * window, because there is none to print. A macro has both.
 *
 * Split rather than composed here because the two do not carry the same
 * weight on screen: the macro is what was CHOSEN and the window is what it
 * currently means, so a control that draws the second more quietly than the
 * first needs them separately. {@link dateRangeLabel} joins them for callers
 * that just want a string.
 *
 * `short` is the window named as the calendar period it is (see
 * {@link wholePeriodLabel}), falling back to the full dates where it is not
 * one. It is what a filter-bar chip prints, where width is scarce; a panel or
 * a standalone trigger has room for `window` and prints that.
 *
 * `running` IS THE WINDOW THE SURFACE IS ACTUALLY ASKING OVER, for a caller
 * that knows it and knows it may have gone stale. A macro is otherwise
 * re-answered here on every render — which is right for a surface that
 * re-answers it too, and wrong for one that resolved it once and kept the
 * answer. The report builder is the second kind: its spec's `from`/`to` are
 * computed when the spec arrives and then held, so a builder left open across
 * a month boundary would print "Oct 2026" over a September report and a
 * September export. Passed in, these dates win for `window` and `short`; the
 * NAME is still the macro's, because the macro is what was chosen and what
 * gets saved. Ignored when it is not a usable window, and irrelevant to a
 * Custom pick, which already shows the dates it stores.
 */
export function dateRangeParts(
  value: DateRangeValue,
  running?: ResolvedDateRange | null,
): {
  name: string;
  window: string;
  short: string;
} {
  const none = { window: '', short: '' };
  if (value.preset === 'all_time') {
    return { name: DATE_RANGE_PRESET_LABELS.all_time, ...none };
  }
  const resolved = resolveDateRange(value);
  if (value.preset === 'custom') {
    // A CUSTOM PICK KEEPS BOTH DATES even when it happens to land on a whole
    // month. It has no macro to sit beside, so its dates are the whole of what
    // the chip says — and "Sep 2026" alone cannot be told apart from the macro
    // that re-answers itself every month, which is the one distinction the bar
    // exists to make.
    const window = resolved ? prettyDateRange(resolved) : '';
    return resolved
      ? { name: '', window, short: window }
      : { name: DATE_RANGE_PRESET_LABELS.custom, ...none };
  }
  if (!resolved) return { name: DATE_RANGE_PRESET_LABELS[value.preset], ...none };
  // Validated through `resolveDateRange` rather than trusted: it is the rule
  // that already decides what a pair of dates has to be, and a caller handing
  // over a blank or inverted window gets the macro re-answered instead of a
  // nonsense one printed.
  const carried = running
    ? resolveDateRange({ preset: 'custom', from: running.from, to: running.to })
    : null;
  const shown = carried ?? resolved;
  const window = prettyDateRange(shown);
  return {
    name: DATE_RANGE_PRESET_LABELS[value.preset],
    window,
    short: wholePeriodLabel(shown) ?? window,
  };
}

/**
 * The one-line summary of a selection, for a popover trigger or a filter chip.
 *
 * A named macro reads as its name ("Previous quarter") because that is what
 * the user chose and it stays true as time passes; a Custom pick has no name,
 * so it reads as its dates. `withRange` appends the resolved window to a
 * macro ("Current month · Aug 1 – Aug 31, 2026"); `running` overrides which
 * window that is, exactly as it does in {@link dateRangeParts}.
 */
export function dateRangeLabel(
  value: DateRangeValue,
  withRange = false,
  running?: ResolvedDateRange | null,
): string {
  const { name, window } = dateRangeParts(value, running);
  if (!name) return window;
  return withRange && window ? `${name} · ${window}` : name;
}
